import { isPlatformBrowser, NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Injector,
    Input,
    OnChanges,
    Output,
    PLATFORM_ID,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { IPagingConfig, Paging } from '../_shared/models/paging.model';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';

@Component({
    selector: 'sirius-pagination',
    templateUrl: './pagination.component.html',
    styleUrls: ['./pagination.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, IconComponent, NgClass, TranslateModule],
})
export class PaginationComponent implements OnChanges {
    private static nextId = 0;

    @Input() config?: IPagingConfig;
    @Input() pageSize?: number;
    @Input() currentPage = 1;
    @Input() allowedSizes?: number[];
    @Input() numberOfPages = 0;
    @Input() totalItems = 0;

    @Output() selectionChange = new EventEmitter<Paging>();

    options: number[] = [];
    isOpen = false;
    activeOptionIndex = 0;

    readonly pagingOptionsId = `sir-pagination-options-${PaginationComponent.nextId++}`;
    readonly pagingTriggerId = `${this.pagingOptionsId}-trigger`;

    @ViewChild('pageIndexTrigger', { read: ElementRef }) pageIndexTrigger?: ElementRef<HTMLButtonElement>;
    @ViewChildren('pageOption', { read: ElementRef }) pageOptions?: QueryList<ElementRef<HTMLButtonElement>>;

    private readonly _elementRef = inject(ElementRef<HTMLElement>);
    private readonly _injector = inject(Injector);
    private readonly _platformId = inject(PLATFORM_ID);

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['config']) {
            this.syncFromInputs();
        }

        if (
            changes['config'] ||
            changes['numberOfPages'] ||
            changes['currentPage'] ||
            changes['allowedSizes'] ||
            changes['pageSize'] ||
            changes['totalItems']
        ) {
            this.rebuildOptions();
            this.activeOptionIndex = this.getCurrentPageIndex();
        }
    }

    setDropdownOpen(isOpen: boolean): void {
        this.isOpen = isOpen;
    }

    onSelect(page: number): void {
        this.selectPage(page);
        this.setDropdownOpen(false);
        this.focusTrigger();
    }

    incrementPageNumber(): void {
        if (this.currentPage < this.numberOfPages) {
            this.selectPage(this.currentPage + 1);
        }
    }

    decrementPageNumber(): void {
        if (this.currentPage > 1) {
            this.selectPage(this.currentPage - 1);
        }
    }

    emit(): void {
        this.selectionChange.emit(
            new Paging({
                currentPage: this.currentPage,
                pageSize: this.pageSize,
                allowedSizes: this.allowedSizes,
                numberOfPages: this.numberOfPages,
                totalItems: this.totalItems,
            })
        );
    }

    @HostListener('document:focusin', ['$event'])
    onDocumentFocusIn(event: FocusEvent): void {
        if (!this.isOpen) return;
        const target = event.target as Node | null;
        if (target && this._elementRef.nativeElement.contains(target)) return;
        this.setDropdownOpen(false);
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        if (!this.isOpen) return;
        const target = event.target as Node | null;
        // Opening re-renders the trigger's contents, so a click that originated on a swapped-out
        // node reaches this listener already detached. A detached target says nothing about where
        // the click landed and must not be read as a click outside the component.
        if (!target || !target.isConnected) return;
        if (this._elementRef.nativeElement.contains(target)) return;
        this.setDropdownOpen(false);
    }

    onTriggerKeydown(event: KeyboardEvent): void {
        const currentIndex = this.getCurrentPageIndex();

        switch (event.key) {
            case ' ':
            case 'Enter':
                event.preventDefault();
                if (this.isOpen) {
                    this.setDropdownOpen(false);
                } else {
                    this.openDropdownAndFocus(currentIndex);
                }
                return;
            case 'ArrowDown':
                event.preventDefault();
                this.openDropdownAndFocus(this.isOpen ? (currentIndex + 1) % this.options.length : currentIndex);
                return;
            case 'ArrowUp':
                event.preventDefault();
                this.openDropdownAndFocus(
                    this.isOpen ? (currentIndex - 1 + this.options.length) % this.options.length : currentIndex
                );
                return;
            case 'Home':
                event.preventDefault();
                this.openDropdownAndFocus(0);
                return;
            case 'End':
                event.preventDefault();
                this.openDropdownAndFocus(this.options.length - 1);
                return;
            case 'Escape':
                if (!this.isOpen) return;
                event.preventDefault();
                this.setDropdownOpen(false);
                this.focusTrigger();
                return;
            default:
                return;
        }
    }

    onOptionKeydown(event: KeyboardEvent, index: number): void {
        const optionsCount = this.options.length;
        if (!optionsCount) return;

        switch (event.key) {
            case 'Enter':
            case ' ':
                event.preventDefault();
                this.onSelect(this.options[index]);
                return;
            case 'ArrowDown':
                event.preventDefault();
                this.focusOption((index + 1) % optionsCount);
                return;
            case 'ArrowUp':
                event.preventDefault();
                this.focusOption((index - 1 + optionsCount) % optionsCount);
                return;
            case 'Home':
                event.preventDefault();
                this.focusOption(0);
                return;
            case 'End':
                event.preventDefault();
                this.focusOption(optionsCount - 1);
                return;
            case 'Tab':
                this.setDropdownOpen(false);
                return;
            case 'Escape':
                event.preventDefault();
                this.setDropdownOpen(false);
                this.focusTrigger();
                return;
            default:
                return;
        }
    }

    private focusOption(index: number): void {
        this.activeOptionIndex = index;
        if (!isPlatformBrowser(this._platformId)) return;
        const option = this.pageOptions?.get(index)?.nativeElement;
        option?.focus();
    }

    private focusTrigger(): void {
        if (!isPlatformBrowser(this._platformId)) return;
        const trigger = this.pageIndexTrigger?.nativeElement;
        trigger?.focus({ preventScroll: true });
    }

    private getCurrentPageIndex(): number {
        const idx = this.options.findIndex((x) => x === this.currentPage);
        return idx >= 0 ? idx : 0;
    }

    private openDropdownAndFocus(index: number): void {
        this.setDropdownOpen(true);
        if (!isPlatformBrowser(this._platformId)) return;
        afterNextRender(() => this.focusOption(index), { injector: this._injector });
    }

    private syncFromInputs(): void {
        if (!this.config) return;

        this.pageSize = this.config.pageSize;
        this.allowedSizes = this.config.allowedSizes;
        this.currentPage = this.config.currentPage;
        this.numberOfPages = this.config.numberOfPages;
        this.totalItems = this.config.totalItems;
    }

    private rebuildOptions(): void {
        this.options = Array.from({ length: this.numberOfPages }, (_, idx) => idx + 1);
    }

    private selectPage(page: number): void {
        if (page === this.currentPage) return;

        this.currentPage = page;
        this.emit();
    }
}
